import { useSafeLayoutEffect } from '@hover/blueprint';
import type { AnimationControls } from 'framer-motion';
import { useAnimation } from 'framer-motion';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface DisappearingHeaderOnScroll {
  headerRef: React.RefObject<HTMLDivElement>;
  headerControls?: AnimationControls;
  topAdjustedComponentControls?: AnimationControls;
}

export const useDisappearingHeaderOnScroll = (): DisappearingHeaderOnScroll => {
  const [hidden, setIsHidden] = useState(false);
  const [height, setHeight] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const headerControls = useAnimation();
  const topAdjustedComponentControls = useAnimation();

  useSafeLayoutEffect(() => {
    if (headerRef.current) {
      setHeight(headerRef.current.clientHeight);
    }
  }, [headerRef?.current?.clientHeight]);

  const handleScroll = useCallback(() => {
    // We need to set an upper threshold, because on mobile there is scroll bounce
    // when you reach the top of the page. This causes the header to be hidden again,
    // even though the user is not scrolling.
    if (window.scrollY < 50) {
      setIsHidden(false);
      return;
    }

    const scrollDirection =
      window.scrollY > lastScrollY.current ? 'down' : 'up';

    lastScrollY.current = window.scrollY;
    setIsHidden(scrollDirection === 'down');
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    const transition = { duration: 0.25, ease: 'easeInOut' };

    void headerControls.start({
      y: hidden ? -height : 0,
      transition,
    });

    void topAdjustedComponentControls.start({
      top: hidden ? 0 : height,
      transition,
    });
  }, [hidden, height, headerControls, topAdjustedComponentControls]);

  return {
    headerRef,
    headerControls,
    topAdjustedComponentControls,
  };
};
